import { useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { platformSession } from '@/core/api/platformApi';
import { useUiStore } from '@/core/auth/uiStore';
import { AppIcon, type AppIconName } from '@/shared/components/AppIcon';

interface PlatformNavigationItem {
  to: string;
  label: string;
  description: string;
  icon: AppIconName;
  permissions: string[];
}

const navigation: PlatformNavigationItem[] = [
  {
    to: '/platform/tenants',
    label: 'شرکت‌ها و لایسنس‌ها',
    description: 'مدیریت حساب‌ها، اشتراک و ظرفیت مصرف',
    icon: 'building',
    permissions: ['platform.companies.read'],
  },
  {
    to: '/platform/base-data',
    label: 'اطلاعات پایه',
    description: 'انواع کسب‌وکار، پلن‌ها و قالب‌ها',
    icon: 'settings',
    permissions: ['platform.base-data.manage', 'platform.plans.manage'],
  },
  {
    to: '/platform/billing',
    label: 'درخواست‌های خرید',
    description: 'تأیید خرید پلن و شارژ پیامک شرکت‌ها',
    icon: 'document',
    permissions: ['platform.licenses.manage'],
  },
  {
    to: '/platform/features',
    label: 'مدیریت قابلیت‌ها',
    description: 'قابلیت‌های پلن و دسترسی اختصاصی شرکت‌ها',
    icon: 'grid',
    permissions: ['platform.features.manage'],
  },
  {
    to: '/platform/guides',
    label: 'راهنمای تعاملی',
    description: 'راهنماهای متناسب با هر کسب‌وکار و نقش',
    icon: 'sparkles',
    permissions: ['platform.guides.manage'],
  },
  {
    to: '/platform/staff',
    label: 'مدیران پلتفرم',
    description: 'کاربران، نقش‌ها و گزارش عملکرد',
    icon: 'users',
    permissions: ['platform.staff.manage', 'platform.reports.read'],
  },
  {
    to: '/platform/announcements',
    label: 'اطلاع‌رسانی شرکت‌ها',
    description: 'ارسال و پیگیری پیام‌های مدیریتی',
    icon: 'bell',
    permissions: ['platform.announcements.manage'],
  },
  {
    to: '/platform/support',
    label: 'پشتیبانی شرکت‌ها',
    description: 'گفت‌وگوهای شرکت‌ها و اتصال ربات بله',
    icon: 'send',
    permissions: ['platform.support.manage', 'platform.bale.manage'],
  },
  {
    to: '/platform/notifications',
    label: 'پیامک و اعلان‌ها',
    description: 'ارائه‌دهنده، ارسال آزمایشی و گزارش تحویل',
    icon: 'send',
    permissions: ['platform.notifications.manage'],
  },
  {
    to: '/platform/message-templates',
    label: 'قالب‌های پیام',
    description: 'متن پیام فرایندهای خودکار پلتفرم',
    icon: 'document',
    permissions: ['platform.notifications.manage'],
  },
  {
    to: '/platform/settings',
    label: 'تنظیمات',
    description: 'هشدارهای سراسری و فضای ذخیره‌سازی',
    icon: 'settings',
    permissions: ['platform.alerts.manage', 'platform.storage.manage'],
  },
];

const initials = (value?: string | null): string =>
  value?.trim().split(/\s+/).slice(0, 2).map((part) => part[0]).join('') || 'پ';

export const PlatformShell = (): React.ReactElement => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const colorMode = useUiStore((state) => state.colorMode);
  const toggleColorMode = useUiStore((state) => state.toggleColorMode);
  const displayName = platformSession.displayName() || 'مدیر پلتفرم';
  const role = platformSession.role();
  const items = useMemo(
    () => navigation.filter((item) => item.permissions.some((permission) => platformSession.hasPermission(permission))),
    [],
  );
  const activeItem = items.find((item) =>
    location.pathname === item.to || location.pathname.startsWith(`${item.to}/`));
  const currentTitle = activeItem?.label || 'مدیریت پلتفرم';
  const currentDescription = activeItem?.description || 'کنترل مرکزی شرکت‌ها و سرویس‌های CRM';
  const roleLabel = role?.toLowerCase() === 'manager' ? 'مدیر زیرساخت' : 'مدیر پلتفرم';

  const goTo = (path: string): void => {
    navigate(path);
    setMobileMenuOpen(false);
    setUserMenuOpen(false);
  };

  const logout = (): void => {
    platformSession.clear();
    navigate('/platform/login', { replace: true });
  };

  const sidebar = (
    <aside className="flex h-full w-72 flex-col border-l border-slate-200 bg-white px-4 py-5 shadow-[0_0_32px_rgba(24,38,76,0.06)]">
      <button className="flex items-center gap-3 px-2 text-right" onClick={() => goTo('/platform/tenants')} type="button">
        <span className="grid size-11 shrink-0 place-items-center rounded-2xl bg-gradient-to-bl from-brand-500 to-violet-500 text-lg font-black text-white shadow-lg shadow-brand-500/25">C</span>
        <span className="min-w-0">
          <span className="block truncate text-base font-black text-ink-950">مدیریت پلتفرم CRM</span>
          <span className="mt-0.5 block truncate text-xs text-slate-400">مرکز عملیات شرکت‌ها و سرویس‌ها</span>
        </span>
      </button>

      <div className="my-6 h-px bg-slate-100" />
      <p className="px-3 text-[11px] font-bold tracking-[0.12em] text-slate-400">کنترل مرکزی</p>
      <nav className="mt-3 flex flex-1 flex-col gap-1 overflow-y-auto pb-2" aria-label="بخش‌های مدیریت پلتفرم">
        {items.map((item) => {
          const active = location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);
          return (
            <button
              className={`flex items-center gap-3 rounded-xl px-3 py-3 text-right text-sm font-bold transition ${active ? 'bg-brand-50 text-brand-600 shadow-sm' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'}`}
              key={item.to}
              onClick={() => goTo(item.to)}
              type="button"
            >
              <AppIcon name={item.icon} size={19} />
              <span className="truncate">{item.label}</span>
            </button>
          );
        })}
        {items.length === 0 ? (
          <p className="rounded-xl bg-amber-50 px-3 py-4 text-xs font-bold leading-6 text-amber-700">
            برای این حساب هنوز دسترسی مدیریتی تعریف نشده است.
          </p>
        ) : null}
      </nav>

      <div className="mt-4 rounded-2xl bg-slate-950 p-4 text-white">
        <div className="flex items-center gap-2 text-sm font-bold">
          <AppIcon name="sparkles" size={18} /> پنل مرکزی
        </div>
        <p className="mt-2 truncate text-xs font-bold text-slate-200">{displayName}</p>
        <p className="mt-1 text-[11px] leading-5 text-slate-400">
          {roleLabel} · {new Intl.NumberFormat('fa-IR').format(items.length)} بخش در دسترس
        </p>
      </div>
    </aside>
  );

  return (
    <div className="crm-shell min-h-screen bg-[#f7f8fc] text-slate-900" dir="rtl">
      <div className="fixed inset-y-0 right-0 z-40 hidden xl:block">{sidebar}</div>

      {mobileMenuOpen ? (
        <button
          aria-label="بستن منوی پلتفرم"
          className="fixed inset-0 z-40 bg-slate-950/35 xl:hidden"
          onClick={() => setMobileMenuOpen(false)}
          type="button"
        />
      ) : null}

      <div className={`fixed inset-y-0 right-0 z-50 transition-transform duration-300 xl:hidden ${mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        {sidebar}
      </div>

      <div className="min-h-screen xl:pr-72">
        <header className="sticky top-0 z-30 flex h-[74px] items-center justify-between border-b border-slate-200/80 bg-white/90 px-4 backdrop-blur md:px-7">
          <div className="flex min-w-0 items-center gap-3">
            <button
              aria-label="بازکردن منوی پلتفرم"
              className="grid size-10 shrink-0 place-items-center rounded-xl border border-slate-200 text-slate-600 xl:hidden"
              onClick={() => setMobileMenuOpen(true)}
              type="button"
            >
              <AppIcon name="menu" />
            </button>
            <div className="min-w-0">
              <p className="truncate text-base font-black text-ink-950 md:text-lg">{currentTitle}</p>
              <p className="hidden truncate text-xs text-slate-400 sm:block">{currentDescription}</p>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2 sm:gap-3">
            <span className="hidden items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-bold text-slate-500 md:inline-flex">
              <AppIcon name="grid" size={16} /> پنل مرکزی
            </span>
            <button
              aria-label={colorMode === 'dark' ? 'فعال‌کردن حالت روشن' : 'فعال‌کردن حالت تاریک'}
              className="grid size-10 place-items-center rounded-xl border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50"
              onClick={toggleColorMode}
              title={colorMode === 'dark' ? 'حالت روشن' : 'حالت تاریک'}
              type="button"
            >
              <AppIcon name={colorMode === 'dark' ? 'sun' : 'moon'} size={18} />
            </button>
            <div className="relative">
              <button
                aria-expanded={userMenuOpen}
                className="flex items-center gap-2 rounded-xl py-1 pr-1 text-right transition hover:bg-slate-50 sm:pl-2"
                onClick={() => setUserMenuOpen((value) => !value)}
                type="button"
              >
                <span className="grid size-9 place-items-center rounded-xl bg-brand-100 text-xs font-black text-brand-600">{initials(displayName)}</span>
                <span className="hidden sm:block">
                  <span className="block max-w-36 truncate text-xs font-bold">{displayName}</span>
                  <span className="block text-[11px] text-slate-400">{roleLabel}</span>
                </span>
              </button>

              {userMenuOpen ? (
                <div className="absolute left-0 top-12 z-50 w-48 rounded-2xl border border-slate-100 bg-white p-2 shadow-xl shadow-slate-900/10">
                  <button
                    className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-right text-sm font-bold text-slate-600 hover:bg-slate-50"
                    onClick={() => goTo('/platform/tenants')}
                    type="button"
                  >
                    <AppIcon name="building" size={17} /> شرکت‌ها و لایسنس‌ها
                  </button>
                  <button
                    className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-right text-sm font-bold text-rose-600 hover:bg-rose-50"
                    onClick={logout}
                    type="button"
                  >
                    <AppIcon name="close" size={17} /> خروج از پنل
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </header>

        <div className="min-w-0"><Outlet /></div>
      </div>
    </div>
  );
};
